import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, User, LogOut, X, Truck, Phone, ChevronDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useState } from "react";

export const Header = () => {
  const { cartCount, setCartOpen, user, logout } = useApp();
  const [open, setOpen] = useState(false);
  const [sweetOpen, setSweetOpen] = useState(false); // mobile-only submenu
  const navigate = useNavigate();

  return (
    <>
      {/* Top announcement bar */}
      <div
        className="text-center text-xs md:text-sm py-2.5 px-4"
        style={{ background: "var(--accent)", color: "#FAF3EB" }}
        data-testid="announcement-bar"
      >
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-3 md:gap-8">
          <span className="inline-flex items-center gap-2">
            <Truck size={14} strokeWidth={1.5} /> Same-day delivery in Mumbai
          </span>
          <span className="hidden md:inline opacity-50">|</span>
          <span className="inline-flex items-center gap-2">
            <Phone size={14} strokeWidth={1.5} /> Call / WhatsApp: +91 98193 00124
          </span>
        </div>
      </div>

      <header
        className="sticky top-0 z-40 backdrop-blur-md border-b"
        style={{ background: "rgba(250, 243, 235, 0.92)", borderColor: "var(--line)" }}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-3 items-center min-h-[7rem] py-2 gap-4">
          {/* Left nav */}
          <nav className="hidden md:flex items-center gap-8 eyebrow">
            {/* Sweet Bakes dropdown */}
            <div className="relative group" data-testid="nav-sweet-bakes-wrap">
              <button
                className="inline-flex items-center gap-1 hover:text-[var(--accent)] transition-colors eyebrow"
                data-testid="nav-sweet-bakes"
                type="button"
              >
                Sweet Bakes
                <ChevronDown size={14} strokeWidth={2} className="transition-transform group-hover:rotate-180" />
              </button>
              <div
                className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity"
                data-testid="sweet-bakes-menu"
              >
                <div
                  className="min-w-[200px] rounded-lg py-2 shadow-lg border"
                  style={{ background: "var(--bg-warm)", borderColor: "var(--line)" }}
                >
                  <Link to="/shop?category=cakes" data-testid="nav-cakes" className="block px-5 py-2.5 hover:bg-[var(--bg-card)] eyebrow whitespace-nowrap">Cakes / Pastry</Link>
                  <Link to="/bundle" data-testid="nav-bundle" className="block px-5 py-2.5 hover:bg-[var(--bg-card)] eyebrow whitespace-nowrap">Build a Bundle</Link>
                  <Link to="/shop?category=tea-cakes" data-testid="nav-tea-cakes" className="block px-5 py-2.5 hover:bg-[var(--bg-card)] eyebrow whitespace-nowrap">Tea Time Cakes</Link>
                  <Link to="/shop?category=cookies" data-testid="nav-cookies" className="block px-5 py-2.5 hover:bg-[var(--bg-card)] eyebrow whitespace-nowrap">Cookies</Link>
                </div>
              </div>
            </div>
            <Link to="/shop?category=artisan-savouries" data-testid="nav-artisan-savouries" className="hover:text-[var(--accent)] transition-colors">Savouries</Link>
          </nav>

          {/* Centered brand */}
          <Link
            to="/"
            className="flex items-center justify-self-start md:justify-self-center min-w-0"
            data-testid="brand-link"
            aria-label="Cocoamaya Patisserie — Home"
          >
            <img
              src="/cocoamaya-bakery.png"
              alt="Cocoamaya Patisserie"
              className="h-12 sm:h-14 md:h-16 lg:h-20 w-auto max-w-[180px] sm:max-w-[220px] md:max-w-none select-none"
              draggable="false"
            />
          </Link>

          {/* Right nav + cart */}
          <div className="flex items-center justify-end gap-5 md:gap-7">
            <Link to="/custom-cake" data-testid="nav-custom" className="hidden md:inline eyebrow hover:text-[var(--accent)] transition-colors">Custom</Link>
            <Link to="/gift-hampers" data-testid="nav-hampers" className="hidden md:inline eyebrow hover:text-[var(--accent)] transition-colors">Hampers</Link>
            <Link to="/track-order" data-testid="nav-track" className="hidden md:inline eyebrow hover:text-[var(--accent)] transition-colors">Track</Link>
            {user ? (
              <div className="hidden md:flex items-center gap-3">
                {user.is_admin && (
                  <Link to="/admin" className="eyebrow hover:text-[var(--accent)]" data-testid="nav-admin">Admin</Link>
                )}
                <button onClick={logout} data-testid="logout-btn" aria-label="Logout">
                  <LogOut size={18} strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <Link to="/login" className="hidden md:inline-flex items-center gap-2 eyebrow hover:text-[var(--accent)]" data-testid="nav-login">
                <User size={16} strokeWidth={1.5} />
              </Link>
            )}
            <button
              onClick={() => setCartOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors"
              style={{ background: "var(--accent)", color: "#FAF3EB" }}
              data-testid="open-cart-btn"
              aria-label="Open cart"
            >
              <ShoppingBag size={16} strokeWidth={1.5} />
              <span data-testid="cart-count">Cart ({cartCount})</span>
            </button>
            <button className="md:hidden" onClick={() => setOpen((o) => !o)} aria-label="Menu" data-testid="mobile-menu-btn">
              {open ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t px-6 py-4 flex flex-col gap-3" style={{ borderColor: "var(--line)" }}>
            <button
              onClick={() => setSweetOpen((s) => !s)}
              className="flex items-center justify-between eyebrow"
              data-testid="mobile-sweet-bakes-toggle"
            >
              Sweet Bakes
              <ChevronDown size={14} strokeWidth={2} className={`transition-transform ${sweetOpen ? "rotate-180" : ""}`} />
            </button>
            {sweetOpen && (
              <div className="pl-4 flex flex-col gap-3 border-l" style={{ borderColor: "var(--line)" }}>
                <Link to="/shop?category=cakes" onClick={() => setOpen(false)} className="eyebrow">Cakes / Pastry</Link>
                <Link to="/bundle" onClick={() => setOpen(false)} className="eyebrow">Build a Bundle</Link>
                <Link to="/shop?category=tea-cakes" onClick={() => setOpen(false)} className="eyebrow">Tea Time Cakes</Link>
                <Link to="/shop?category=cookies" onClick={() => setOpen(false)} className="eyebrow">Cookies</Link>
              </div>
            )}
            <Link to="/shop?category=artisan-savouries" onClick={() => setOpen(false)} className="eyebrow">Artisan Savouries</Link>
            <Link to="/custom-cake" onClick={() => setOpen(false)} className="eyebrow">Custom Cake</Link>
            <Link to="/gift-hampers" onClick={() => setOpen(false)} className="eyebrow">Gift Hampers</Link>
            <Link to="/track-order" onClick={() => setOpen(false)} className="eyebrow">Track Order</Link>
            {user?.is_admin && <Link to="/admin" onClick={() => setOpen(false)} className="eyebrow">Admin</Link>}
            {user ? (
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="eyebrow text-left">Logout</button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="eyebrow">Sign in</Link>
            )}
          </div>
        )}
      </header>
    </>
  );
};
